import React, { useState, useRef, useEffect } from "react";
import { sendChatMessage } from "../services/api";
import "./ChatGPTStyleInterface.css";

const ChatGPTStyleInterface = () => {
  const [messages, setMessages] = useState([
    {
      text: "Hello! I'm your AI Assistant. I can help you with scheduling, research, tasks, and more. How can I assist you today?",
      sender: "assistant",
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Scroll to bottom whenever messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
      inline: "nearest",
    });
  }, [messages]);

  // Focus input on load
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    // Add user message to chat
    const userMessage = { text: input, sender: "user" };
    setMessages((prevMessages) => [...prevMessages, userMessage]);

    // Clear input and show loading state
    setInput("");
    setIsLoading(true);

    try {
      // Send request to your backend API using the API service
      const data = await sendChatMessage(input);

      // Add assistant response to chat
      setMessages((prevMessages) => [
        ...prevMessages,
        { text: data.response, sender: "assistant", taskType: data.taskType },
      ]);
    } catch (error) {
      console.error("Error:", error);
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          text: "Sorry, I couldn't process your request. Please try again later.",
          sender: "assistant",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickPrompt = (prompt) => {
    setInput(prompt);
    inputRef.current?.focus();
  };

  return (
    <div className="chatgpt-interface">
      <div className="chat-header">
        <span className="chat-header-icon">🤖</span>
        <h2 className="chat-header-title">Agent Samur.ai Assistant</h2>
      </div>
      <div className="chat-container" ref={messagesContainerRef}>
        <div className="messages-wrapper">
          {messages.map((message, index) => (
            <div key={index} className={`message ${message.sender}`}>
              <div className="message-content">
                {message.sender === "assistant" && (
                  <div className="assistant-icon">
                    {message.taskType
                      ? getIconForTaskType(message.taskType)
                      : "🤖"}
                  </div>
                )}
                <div className="message-text">
                  {typeof message.text === "string"
                    ? formatMessageWithLinks(message.text)
                    : message.text}
                </div>
                {message.sender === "user" && (
                  <div className="user-icon">You</div>
                )}
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="message assistant">
              <div className="message-content">
                <div className="assistant-icon">🤖</div>
                <div className="message-text">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <div className="examples-section">
        <div className="examples-container">
          <h3>Sample Queries</h3>
          <div className="examples-grid">
            <div
              className="example-card"
              onClick={() =>
                handleQuickPrompt(
                  "Schedule a meeting with John tomorrow at 3pm"
                )
              }
            >
              <div className="example-icon">📅</div>
              <div className="example-text">
                Schedule a meeting with John tomorrow at 3pm
              </div>
            </div>
            <div
              className="example-card"
              onClick={() =>
                handleQuickPrompt("What's on my calendar for next week?")
              }
            >
              <div className="example-icon">📆</div>
              <div className="example-text">
                What's on my calendar for next week?
              </div>
            </div>
            <div
              className="example-card"
              onClick={() =>
                handleQuickPrompt("Add buy groceries to my task list")
              }
            >
              <div className="example-icon">✅</div>
              <div className="example-text">
                Add buy groceries to my task list
              </div>
            </div>
            <div
              className="example-card"
              onClick={() =>
                handleQuickPrompt("What are the current Bitcoin prices?")
              }
            >
              <div className="example-icon">💰</div>
              <div className="example-text">
                What are the current Bitcoin prices?
              </div>
            </div>
            <div
              className="example-card"
              onClick={() =>
                handleQuickPrompt(
                  "Create a research report about renewable energy"
                )
              }
            >
              <div className="example-icon">📚</div>
              <div className="example-text">
                Create a research report about renewable energy
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="input-section">
        <form className="input-form" onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={handleInputChange}
            placeholder="Message Agent Samur.ai..."
            disabled={isLoading}
            className="message-input"
          />
          <button
            type="submit"
            disabled={isLoading || !input.trim()}
            className="send-button"
            aria-label="Send message"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22 2L11 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M22 2L15 22L11 13L2 9L22 2Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </form>
        <div className="disclaimer">
          Agent Samur.ai can make mistakes. Check important information.
        </div>
      </div>
    </div>
  );
};

// Helper function to get icon based on task type
function getIconForTaskType(taskType) {
  const icons = {
    scheduling: "📅",
    research: "📚",
    task: "✅",
    communication: "💬",
    project: "📁",
    content: "📰",
    update: "📢",
    general: "🤖",
  };

  return icons[taskType] || "🤖";
}

// Add this function above or below getIconForTaskType
const formatMessageWithLinks = (text) => {
  // Regular expression to detect URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;

  // If no URLs in the text, return the original text
  if (!urlRegex.test(text)) {
    return text;
  }

  // Split the text by URLs and create an array of text and link elements
  const parts = text.split(urlRegex);
  const matches = text.match(urlRegex);

  return parts.map((part, index) => {
    // Even indices are regular text
    if (index % 2 === 0) {
      return part;
    }
    // Odd indices are URLs, replace with link elements
    const url = matches[(index - 1) / 2];
    return (
      <a
        key={`link-${index}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="message-link"
      >
        {url}
      </a>
    );
  });
};

export default ChatGPTStyleInterface;
